import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import dnPopup from "./utils/dnPopup";
import { FaPaperclip, FaDownload, FaFilePdf, FaFileExcel } from "react-icons/fa";
import { apiFetch, API_BASE } from "./utils/api";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ExcelJS from "exceljs";
import logo_eagle from "./assets/img/logo_eagle.png";
import DOLLUZ_CORP from "./assets/img/DOLLUZ_CORP.png";
import { isWeekend } from "./utils/workweek";
import "./Leave_approvals.css";

// "1 day" / "2 days" / "0.5 days" — 1 is singular, everything else (including
// half-days) is plural.
const fmtDays = (n) => `${Number(n)} ${Number(n) === 1 ? "day" : "days"}`;
const isAre = (n) => (Number(n) === 1 ? "is" : "are");

// Requested leave days — mirrors the backend balance rule exactly:
//  • Saturdays and Sundays are the weekly offs and don't count.
//  • A half-day START subtracts 0.5.
//  • A half-day END subtracts 0.5 ONLY when it's a different day — otherwise a
//    single-day half-day leave would wrongly compute 1 - 0.5 - 0.5 = 0.
const requestedDays = (leave) => {
    const start = new Date(leave.start_date);
    const end = new Date(leave.end_date);
    if (isNaN(start) || isNaN(end)) return 0;
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    if (end < start) return 0;

    let days = 0;
    for (const d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        if (!isWeekend(d)) days += 1; // skip weekends
    }

    const sameDay = start.getTime() === end.getTime();
    if (leave.start_date_breakdown && leave.start_date_breakdown !== "Full") days -= 0.5;
    if (!sameDay && leave.end_date_breakdown && leave.end_date_breakdown !== "Full") days -= 0.5;
    return Math.max(0, days);
};

const Leave_approvals = ({ navSize }) => {
    const [leaveRequests, setLeaveRequests] = useState([]);
    const [filteredRequests, setFilteredRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectReason, setRejectReason] = useState("");
    const [selectedLeaveId, setSelectedLeaveId] = useState(null);
    const [selectedFilter, setSelectedFilter] = useState("leave_status");
    const [filters, setFilters] = useState({ leave_status: [] });
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedIds, setSelectedIds] = useState([]);
    const [isBulkReject, setIsBulkReject] = useState(false);
    // Approve-review panel: balance + history + the paid/unpaid decision.
    const [showApproveModal, setShowApproveModal] = useState(false);
    const [approveLoading, setApproveLoading] = useState(false);
    const [approveCtx, setApproveCtx] = useState(null);
    const [payChoice, setPayChoice] = useState("Auto");
    const rowsPerPage = 10;
    const [loggedInEmp, setLoggedInEmp] = useState(null);
    const navigate = useNavigate();
    const [accessLevels, setAccessLevels] = useState([]);

    useEffect(() => {
        fetchAccessLevels();
        fetchloginedEmployees();
    }, [navigate]);

    const fetchloginedEmployees = async () => {
        try {
            const res = await apiFetch(`/api/employee/logined_employee`, {
                method: 'GET',
                credentials: 'include',
            });
            const data = await res.json();

            if (data?.message === "Access Denied. No Token Provided!" ||
                data?.message === "Invalid Token") {
                navigate("/login");
                return;
            }

            if (data[0].emp_id) {
                setLoggedInEmp(data[0]);
                fetchLeaveRequests();
                // No access to this page → send to an allowed page, not the login screen
                if (loggedInEmp && accessLevels.length > 0) {
                    if (!hasPageAccess("Leave Approvals")) {
                        navigate("/My_leave_requests");
                    }
                }
            }

        } catch (err) {
            console.error("Error fetching employees:", err);
        }
    };

    const hasPageAccess = (pageName) => {
        if (!loggedInEmp || accessLevels.length === 0) return false;

        const role = loggedInEmp.emp_access_level; // Admin, Sub Admin, Manager, User

        const page = accessLevels.find(p => p.page_name === pageName);
        if (!page) return false;

        if (role === "Admin") return page.admin_access === 1;
        if (role === "Sub Admin") return page.subadmin_access === 1;
        if (role === "Manager") return page.manager_access === 1;
        if (role === "User") return page.user_access === 1;

        return false;
    };

    const fetchAccessLevels = async () => {
        try {
            const res = await apiFetch(`/api/login/get-access`, {
                method: 'GET',
                credentials: 'include',
            });
            const data = await res.json();
            if (data.success) {
                setAccessLevels(data.data);
            }
        } catch (err) {
            console.error("❌ Error fetching access levels:", err);
        }
    };

    // ✅ Fetch leave requests
    const fetchLeaveRequests = async () => {
        try {
            const res = await apiFetch(`/api/leave/leave_requests_list_filtered`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: 'include',
            });

            const data = await res.json();
            setLeaveRequests(Array.isArray(data) ? data : []);
            setFilteredRequests(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("❌ Error fetching leave requests:", err);
        } finally {
            setLoading(false);
        }
    };

    // 🔹 Filter Options
    const filterOptions = [
        { value: "leave_status", label: "Status" },
        { value: "leave_type", label: "Leave Type" },
        { value: "emp_name", label: "Employee" },
    ];

    const getOptions = (key) => {
        const values = [...new Set(leaveRequests.map((req) => req[key] || "-"))];
        return values.map((v) => ({ value: v, label: v }));
    };

    // 🔹 Filtering Logic
    useEffect(() => {
        const filterableColumns = Object.keys(filters);
        const filtered = leaveRequests.filter((req) =>
            filterableColumns.every((col) => {
                if (!filters[col] || filters[col].length === 0) return true;
                return filters[col].some((f) => f.value === req[col]);
            })
        );
        setFilteredRequests(filtered);
        setCurrentPage(1);
    }, [filters, leaveRequests]);

    // 🔹 Pagination
    const totalPages = Math.ceil(filteredRequests.length / rowsPerPage);
    const paginatedRequests = filteredRequests.slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage
    );

    // ✅ Approve — opens the review panel rather than a bare yes/no, because
    //    approving is also where the paid/unpaid call gets made. The approver
    //    sees this year's balance and recent history before deciding.
    const handleApprove = async (id) => {
        setApproveCtx(null);
        setPayChoice("Auto");
        setShowApproveModal(true);
        setApproveLoading(true);
        try {
            const res = await apiFetch(`/api/Leave/approval_context/${id}`, { credentials: "include" });
            const data = await res.json();
            if (!res.ok || !data.success) {
                setShowApproveModal(false);
                dnPopup.error("Couldn't load request", data.message || "Please try again.");
                return;
            }
            setApproveCtx(data);
        } catch (e) {
            setShowApproveModal(false);
            dnPopup.error("Couldn't load request", "Please try again.");
        } finally {
            setApproveLoading(false);
        }
    };

    const confirmApprove = () => {
        if (!approveCtx) return;
        setShowApproveModal(false);
        handleStatusUpdate(approveCtx.request.leave_requests_id, "Approved", null, payChoice);
    };

    // ✅ Reject Modal Handling
    const handleReject = (id) => {
        setSelectedLeaveId(id);
        setRejectReason("");
        setShowRejectModal(true);
    };

    const confirmReject = async () => {
        if (!rejectReason.trim()) {
            dnPopup.fire("Please enter a reason before rejecting.", "", "warning");
            return;
        }

        if (isBulkReject) {
            // ✅ Bulk reject mode
            for (const id of selectedIds) {
                await handleStatusUpdate(id, "Rejected", rejectReason);
            }
            dnPopup.fire("Selected leaves rejected successfully.", "", "success");
            setSelectedIds([]);
        } else {
            // ✅ Single reject mode
            handleStatusUpdate(selectedLeaveId, "Rejected", rejectReason);
        }

        setShowRejectModal(false);
        setIsBulkReject(false);
    };

    // ✅ Common Update Function
    //    payOverride: "Auto" (entitlement decides — the default and what bulk
    //    actions always use), "Paid" or "Unpaid" to force it for this request.
    const handleStatusUpdate = async (id, status, reason = null, payOverride = "Auto") => {
        try {
            const res = await apiFetch(`/api/leave/update_status/${id}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ status, reason, pay_override: payOverride }),
            });

            if (res.ok) {
                dnPopup.fire(`Leave ${status.toLowerCase()} successfully.`, "", "success");
                fetchLeaveRequests();
            } else {
                dnPopup.fire("Failed to update status.", "", "error");
            }
        } catch (err) {
            console.error("Error updating leave status:", err);
        }
    };

    const handleBulkAction = async (status) => {
        if (selectedIds.length === 0) return;

        if (status === "Rejected") {
            setIsBulkReject(true);
            setShowRejectModal(true);
            return; // ❌ Don't proceed here — handled by modal
        }

        const confirm = await dnPopup.fire({
            title: `Are you sure to ${status.toLowerCase()} selected leaves?`,
            icon: "question",
            showCancelButton: true,
            confirmButtonText: `Yes, ${status}`,
            cancelButtonText: "Cancel",
        });

        if (confirm.isConfirmed) {
            for (const id of selectedIds) {
                await handleStatusUpdate(id, status);
            }
            dnPopup.fire(`Selected leaves ${status.toLowerCase()} successfully.`, "", "success");
            setSelectedIds([]);
        }
    };

    // ✅ Export to PDF Function (SweetAlert + Only Selected Rows)
    const exportToPDF = (leaveRequests) => {
        let exportData =
            selectedIds.length > 0
                ? leaveRequests.filter((r) => selectedIds.includes(r.leave_requests_id))
                : [];

        if (exportData.length === 0) {
            dnPopup.fire("No rows selected!", "Please select at least one leave request to export.", "warning");
            return;
        }

        const doc = new jsPDF("landscape");
        const pageWidth = doc.internal.pageSize.getWidth();

        autoTable(doc, {
            head: [[
                "Request ID",
                "Employee",
                "Leave Type",
                "Start Date (Breakdown)",
                "End Date (Breakdown)",
                "Requested Days",
                "Description",
                "Attachment",
                "Status"
            ]],
            body: exportData.map((leave) => {
                const diffDays = requestedDays(leave);

                return [
                    leave.leave_requests_id,
                    leave.emp_name || leave.emp_id,
                    leave.leave_type || "-",
                    `${new Date(leave.start_date).toLocaleDateString()} (${leave.start_date_breakdown})`,
                    `${new Date(leave.end_date).toLocaleDateString()} (${leave.end_date_breakdown})`,
                    diffDays.toFixed(1),
                    leave.leave_description || "-",
                    leave.attachment ? "Yes" : "No",
                    leave.leave_status || "Pending"
                ];
            }),
            startY: 55,
            margin: { top: 50 },
            theme: "grid",
            styles: { fontSize: 7, cellWidth: "wrap", overflow: "linebreak" },
            headStyles: { halign: "center", fillColor: [41, 128, 185] },
            columnStyles: { 6: { cellWidth: 40 } },
            didDrawPage: function () {
                const imgWidth = 60;
                const imgHeight = 20;
                const imgX = (pageWidth - imgWidth) / 2;
                const imgY = 5;

                doc.addImage(DOLLUZ_CORP, "PNG", imgX, imgY, imgWidth, imgHeight);
                doc.setFontSize(13);
                doc.text("Leave Requests Report", pageWidth / 2, imgY + imgHeight + 8, { align: "center" });
                doc.setFontSize(9);
                doc.text("Generated by dAdmin", pageWidth / 2, imgY + imgHeight + 16, { align: "center" });
            },
        });

        doc.save("Leave_Requests_Report.pdf");
        dnPopup.fire("Exported!", "Selected leave requests exported as PDF.", "success");
    };

    // ✅ Export to Excel Function (SweetAlert + Only Selected Rows)
    const exportToExcel = async (leaveRequests) => {
        let exportData =
            selectedIds.length > 0
                ? leaveRequests.filter((r) => selectedIds.includes(r.leave_requests_id))
                : [];

        if (exportData.length === 0) {
            dnPopup.fire("No rows selected!", "Please select at least one leave request to export.", "warning");
            return;
        }

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Leave Requests");

        // ✅ Add Logo
        const imageResponse = await fetch(DOLLUZ_CORP);
        const imageBlob = await imageResponse.blob();
        const imageBuffer = await imageBlob.arrayBuffer();

        const imageId = workbook.addImage({
            buffer: imageBuffer,
            extension: "png",
        });

        worksheet.addImage(imageId, { tl: { col: 0, row: 0 }, ext: { width: 180, height: 80 } });

        worksheet.addRow([]);
        worksheet.addRow([]);
        worksheet.addRow([]);

        worksheet.mergeCells("A4:H4");
        worksheet.getCell("A4").value = "Leave Requests Report";
        worksheet.getCell("A4").font = { size: 14, bold: true };
        worksheet.getCell("A4").alignment = { horizontal: "center" };

        worksheet.mergeCells("A5:H5");
        worksheet.getCell("A5").value = "Generated by dAdmin";
        worksheet.getCell("A5").font = { size: 10, italic: true };
        worksheet.getCell("A5").alignment = { horizontal: "center" };

        worksheet.addRow([]);

        const headers = [
            "Request ID",
            "Employee",
            "Leave Type",
            "Start Date (Breakdown)",
            "End Date (Breakdown)",
            "Requested Days",
            "Description",
            "Attachment",
            "Status",
        ];

        const headerRow = worksheet.addRow(headers);
        headerRow.eachCell((cell) => {
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "8DB4E2" } };
            cell.font = { bold: true };
            cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
            cell.border = {
                top: { style: "thin" },
                bottom: { style: "thin" },
                left: { style: "thin" },
                right: { style: "thin" },
            };
        });

        exportData.forEach((leave) => {
            const diffDays = requestedDays(leave);

            const row = [
                leave.leave_requests_id,
                leave.emp_name || leave.emp_id,
                leave.leave_type || "-",
                `${new Date(leave.start_date).toLocaleDateString()} (${leave.start_date_breakdown})`,
                `${new Date(leave.end_date).toLocaleDateString()} (${leave.end_date_breakdown})`,
                diffDays.toFixed(1),
                leave.leave_description || "-",
                leave.attachment ? leave.attachment.split("/").pop() : "No File",
                leave.leave_status || "Pending",
            ];

            const dataRow = worksheet.addRow(row);
            dataRow.eachCell((cell) => {
                cell.alignment = { vertical: "top", wrapText: true };
                cell.border = {
                    top: { style: "thin" },
                    bottom: { style: "thin" },
                    left: { style: "thin" },
                    right: { style: "thin" },
                };
            });
        });

        worksheet.columns.forEach((col) => {
            let maxLength = 10;
            col.eachCell({ includeEmpty: true }, (cell) => {
                const len = cell.value ? cell.value.toString().length : 10;
                maxLength = Math.max(maxLength, len + 2);
            });
            col.width = Math.min(maxLength, 40);
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `Leave_Requests_Report.xlsx`;
        link.click();

        dnPopup.fire("Exported!", "Selected leave requests exported as Excel.", "success");
    };

    return (
        <div className="leave-requests-container">
            <div className={`leave-requests-dashboard ${navSize}`}>
                <div className="header-wrapper">
                    <h2>
                        <a className="navbar-brand" href="#">
                            <img src={logo_eagle} alt="dTime Logo" className="page-logo" />
                        </a>
                        Leave Approval
                    </h2>
                </div>

                <div className="leave-requests-content">
                    <div className="leave-filters-container">
                        <div className="filters-left">
                            <Select
                                options={filterOptions}
                                value={filterOptions.find((f) => f.value === selectedFilter)}
                                onChange={(val) => {
                                    setSelectedFilter(val.value);
                                    setFilters((prev) => ({ ...prev, [val.value]: [] }));
                                    setCurrentPage(1);
                                }}
                                placeholder="Select Filter"
                                isSearchable
                                menuPortalTarget={document.body}
                                styles={{
                                    container: (provided) => ({ ...provided, width: 220 }),
                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                }}
                            />
                            <Select
                                placeholder={`Filter by ${filterOptions.find((f) => f.value === selectedFilter).label}`}
                                options={getOptions(selectedFilter)}
                                value={filters[selectedFilter]}
                                onChange={(vals) =>
                                    setFilters((prev) => ({ ...prev, [selectedFilter]: vals || [] }))
                                }
                                closeMenuOnSelect={false}
                                isMulti
                                isClearable
                                menuPortalTarget={document.body}
                                styles={{
                                    container: (provided) => ({ ...provided, width: 250 }),
                                    multiValue: (provided) => ({ ...provided, backgroundColor: "#e0e0e0" }),
                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                }}
                            />

                            <div className="bulk-actions">
                                <button
                                    className="approve-btn"
                                    onClick={() => handleBulkAction("Approved")}
                                    disabled={selectedIds.length === 0}
                                >
                                    Bulk Approve
                                </button>

                                <button
                                    className="reject-btn"
                                    onClick={() => {
                                        setIsBulkReject(true);
                                        setShowRejectModal(true);
                                        setRejectReason("");
                                    }}
                                    disabled={selectedIds.length === 0}
                                >
                                    Bulk Reject
                                </button>

                                {/* Export icons */}
                                <FaDownload
                                    size={20}
                                    style={{ color: "#333" }}
                                    title="Download"
                                />
                                <FaFilePdf
                                    size={24}
                                    color="#e74c3c"
                                    title="Export as PDF"
                                    onClick={() => exportToPDF(filteredRequests)}
                                />
                                <FaFileExcel
                                    size={24}
                                    color="#27ae60"
                                    title="Export as Excel"
                                    onClick={() => exportToExcel(filteredRequests)}
                                />
                            </div>
                        </div>
                    </div>

                    {loading ? (
                        <p className="loading-text">Loading leave requests...</p>
                    ) : (
                        <div className="table-wrapper">
                            <table className="leave-table">
                                <thead>
                                    <tr>
                                        <th>
                                            <input
                                                type="checkbox"
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setSelectedIds(paginatedRequests.map((r) => r.leave_requests_id));
                                                    } else {
                                                        setSelectedIds([]);
                                                    }
                                                }}
                                                checked={
                                                    paginatedRequests.length > 0 &&
                                                    selectedIds.length === paginatedRequests.length
                                                }
                                            />
                                        </th>
                                        <th>ID</th>
                                        <th>Employee</th>
                                        <th>Leave Type</th>
                                        <th>Start Date</th>
                                        <th>End Date</th>
                                        <th>Requested Days</th>
                                        <th>Description</th>
                                        <th>Attachment</th>
                                        <th>Status</th>
                                        <th>Approval</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedRequests.length > 0 ? (
                                        paginatedRequests.map((leave) => {
                                            const diffDays = requestedDays(leave);

                                            return (
                                                <tr key={leave.leave_requests_id}>
                                                    {/* ✅ Selection Checkbox */}
                                                    <td style={{ textAlign: "center" }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedIds.includes(leave.leave_requests_id)}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    setSelectedIds((prev) => [...prev, leave.leave_requests_id]);
                                                                } else {
                                                                    setSelectedIds((prev) =>
                                                                        prev.filter((id) => id !== leave.leave_requests_id)
                                                                    );
                                                                }
                                                            }}
                                                        />
                                                    </td>

                                                    <td>{leave.leave_requests_id}</td>
                                                    <td>{leave.emp_name || leave.emp_id}</td>
                                                    <td>{leave.leave_type || "-"}</td>
                                                    <td>
                                                        {new Date(leave.start_date).toLocaleDateString()} (
                                                        {leave.start_date_breakdown})
                                                    </td>
                                                    <td>
                                                        {new Date(leave.end_date).toLocaleDateString()} (
                                                        {leave.end_date_breakdown})
                                                    </td>
                                                    <td style={{ textAlign: "center" }}>{diffDays.toFixed(1)}</td>

                                                    <td
                                                        style={{
                                                            maxWidth: "200px",
                                                            whiteSpace: "nowrap",
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                        }}
                                                        title={leave.leave_description}
                                                    >
                                                        {leave.leave_description || "-"}
                                                    </td>

                                                    <td style={{ textAlign: "center" }}>
                                                        {leave.attachment ? (
                                                            <a
                                                                href={`${API_BASE}/api/leave/attachment/${leave.leave_requests_id}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                style={{ color: "#4a90e2", fontSize: "18px" }}
                                                            >
                                                                <FaPaperclip />
                                                            </a>
                                                        ) : (
                                                            <span style={{ color: "#999" }}>No File</span>
                                                        )}
                                                    </td>

                                                    <td className="status-cell">
                                                        <span
                                                            className={`status-pill status-${leave.leave_status?.toLowerCase()}`}
                                                        >
                                                            {leave.leave_status || "Pending"}
                                                        </span>
                                                    </td>

                                                    <td style={{ textAlign: "center" }}>
                                                        {leave.leave_status?.toLowerCase() === "pending" ? (
                                                            <>
                                                                <button
                                                                    className="approve-btn"
                                                                    onClick={() => handleApprove(leave.leave_requests_id)}
                                                                >
                                                                    Approve
                                                                </button>
                                                                <button
                                                                    className="reject-btn"
                                                                    onClick={() => handleReject(leave.leave_requests_id)}
                                                                >
                                                                    Reject
                                                                </button>
                                                            </>
                                                        ) : (
                                                            "-"
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan="11" style={{ textAlign: "center" }}>
                                                No leave requests found
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* ✅ Reject Modal */}
            {showApproveModal && (
                <div className="modal-overlay">
                    {/* Enter approves, Escape cancels. autoFocus puts the keyboard
                        focus inside the dialog so both actually reach it. */}
                    <div
                        className="modal-box approve-box"
                        tabIndex={-1}
                        autoFocus
                        onKeyDown={(e) => {
                            if (e.key === "Escape") { e.preventDefault(); setShowApproveModal(false); }
                            if (e.key === "Enter" && approveCtx) { e.preventDefault(); confirmApprove(); }
                        }}
                    >
                        <h3>
                            Approve leave
                            <button
                                type="button"
                                className="appr-close"
                                onClick={() => setShowApproveModal(false)}
                                aria-label="Close"
                            >
                                ✕
                            </button>
                        </h3>

                        {approveLoading || !approveCtx ? (
                            <p className="appr-loading">Loading…</p>
                        ) : (
                            <div className="appr-body">
                                {/* Who + what, as one compact card. The label/value list this
                                    replaced cost four rows of height and pushed the buttons
                                    below the fold. */}
                                <div className="appr-who">
                                    <div className="appr-who-name">
                                        {approveCtx.request.emp_name}
                                        <span className="appr-muted"> ({approveCtx.request.emp_id})</span>
                                    </div>
                                    <div className="appr-who-meta">
                                        <span className="appr-chip">{approveCtx.request.leave_type}</span>
                                        {/* Breakdown is ALWAYS shown, including "Full" — the
                                            approver shouldn't have to infer that a missing
                                            label means a whole day. */}
                                        <span className="appr-when">
                                            {approveCtx.request.start_date} ({approveCtx.request.start_date_breakdown || "Full"})
                                            {approveCtx.request.end_date !== approveCtx.request.start_date &&
                                                ` → ${approveCtx.request.end_date} (${approveCtx.request.end_date_breakdown || "Full"})`}
                                        </span>
                                        <strong className="appr-qty">{fmtDays(approveCtx.request.requested_days)}</strong>
                                    </div>
                                    {approveCtx.request.leave_description && (
                                        <div className="appr-who-reason">{approveCtx.request.leave_description}</div>
                                    )}
                                </div>

                                {/* Value first, label under it — the numbers are what gets
                                    scanned, and the year lives in the label so no separate
                                    heading row is needed. */}
                                <div className="appr-stats">
                                    <div className="appr-stat">
                                        <span className="appr-stat-value">{approveCtx.balance.entitled_days}</span>
                                        <span className="appr-stat-label">Max leaves</span>
                                    </div>
                                    <div className="appr-stat">
                                        <span className="appr-stat-value">{approveCtx.balance.used_days}</span>
                                        <span className="appr-stat-label">Used this year</span>
                                    </div>
                                    <div className={`appr-stat is-balance${approveCtx.balance.remaining_days <= 0 ? " is-empty" : ""}`}>
                                        <span className="appr-stat-value">{approveCtx.balance.remaining_days}</span>
                                        <span className="appr-stat-label">Balance left ({approveCtx.balance.year})</span>
                                    </div>
                                </div>

                                {approveCtx.auto_split.unpaid_days > 0 && (
                                    <p className="appr-auto-note">
                                        They don't have enough balance left. If you leave it on{" "}
                                        <strong>Automatic</strong>, {fmtDays(approveCtx.auto_split.paid_days)}{" "}
                                        {isAre(approveCtx.auto_split.paid_days)} paid and{" "}
                                        <strong>{fmtDays(approveCtx.auto_split.unpaid_days)}{" "}
                                            {isAre(approveCtx.auto_split.unpaid_days)} unpaid (Loss of Pay)</strong>.
                                    </p>
                                )}

                                <section className="appr-pay">
                                    <h4>Pay decision</h4>
                                    <div className="appr-pay-opts">
                                        {(() => {
                                            // Wording follows the request size: a 1-day request should
                                            // never read "Pay for all days".
                                            const n = approveCtx.request.requested_days;
                                            const one = Number(n) === 1;
                                            return [
                                                ["Auto", "Automatic (normal)",
                                                    one
                                                        ? "Pay this day if they still have balance left. If not, it's unpaid."
                                                        : "Pay the days they still have balance for. Anything extra is unpaid."],
                                                ["Paid", one ? "Pay for this day" : "Pay for all days",
                                                    one
                                                        ? "Pay this day in full, even if they have no balance left."
                                                        : "Pay every day in full, even if they have no balance left."],
                                                ["Unpaid", one ? "Don't pay for this day" : "Don't pay for these days",
                                                    one
                                                        ? "This day is unpaid (Loss of Pay). Their balance is not used up."
                                                        : "Every day is unpaid (Loss of Pay). Their balance is not used up."],
                                            ];
                                        })().map(([value, label, hint]) => (
                                            <label key={value} className={`appr-pay-opt${payChoice === value ? " selected" : ""}`}>
                                                <input
                                                    type="radio"
                                                    name="payChoice"
                                                    value={value}
                                                    checked={payChoice === value}
                                                    onChange={() => setPayChoice(value)}
                                                />
                                                <span className="appr-pay-text">
                                                    <span className="appr-pay-label">{label}</span>
                                                    <span className="appr-pay-hint">{hint}</span>
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                </section>

                                {approveCtx.history.length > 0 && (
                                    // Collapsed by default — it's reference, not part of the
                                    // decision, and expanded it pushed Approve below the fold.
                                    // Native <details> so it toggles without state and stays
                                    // keyboard-operable.
                                    <details className="appr-history-toggle">
                                        <summary>Recent leave history ({approveCtx.history.length})</summary>
                                        <div className="appr-history">
                                            <table>
                                                <thead>
                                                    <tr><th>Type</th><th>From</th><th>To</th><th>Status</th><th>Paid?</th></tr>
                                                </thead>
                                                <tbody>
                                                    {approveCtx.history.map(h => (
                                                        <tr key={h.leave_requests_id}>
                                                            <td>{h.leave_type}</td>
                                                            <td>{h.start_date}</td>
                                                            <td>{h.end_date}</td>
                                                            <td>{h.leave_status}</td>
                                                            <td>{h.pay_override === "Paid" ? "Paid" : h.pay_override === "Unpaid" ? "Unpaid" : "Automatic"}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </details>
                                )}
                            </div>
                        )}

                        <div className="modal-buttons">
                            <button className="cancel-btn" onClick={() => setShowApproveModal(false)}>
                                Cancel
                            </button>
                            <button className="approve-btn" onClick={confirmApprove} disabled={!approveCtx}>
                                Approve
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showRejectModal && (
                <div className="modal-overlay">
                    <div
                        className="modal-box"
                        onKeyDown={(e) => {
                            if (e.key === "Escape") { e.preventDefault(); setShowRejectModal(false); }
                            // Ctrl/Cmd+Enter, not plain Enter — the reason is a
                            // textarea and must still accept line breaks.
                            if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); confirmReject(); }
                        }}
                    >
                        <h3>Reason for Rejection</h3>
                        <textarea
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            placeholder="Enter reason here..."
                            rows="4"
                        />
                        <div className="modal-buttons">
                            <button className="reject-btn" onClick={confirmReject}>
                                Reject
                            </button>
                            <button className="cancel-btn" onClick={() => setShowRejectModal(false)}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Leave_approvals;
